import { useCallback } from 'react';
import useExpandCollapsedLayers from '@components/MainStage/hooks/useExpandCollapsedLayers';
import {
  getMultiSelectionState,
  useMultiSelectionActions,
} from '@components/MainStage/multiSelection.store';
import { canvasState } from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import { useProject } from '@context/Project';
import { canvasMap } from '@utils/canvas/CanvasMap';
import { isElementLocal } from '@utils/drawers';

/**
 * Shift-click multi-selection entry / continuation logic shared by the canvas
 * and the entities list.
 *
 * - Entering multi-selection from a single opened entity (drawer open):
 *   closes the drawer following the unsaved-changes handling, then seeds the
 *   multi-selection with the previously opened entity (the anchor) and the
 *   shift-clicked one.
 * - Entering multi-selection by holding Shift before clicking: starts the
 *   multi-selection immediately on the first shift-click.
 * - Continuing to build: adds a not-yet-selected entity, or removes it if it is
 *   already part of the selection (deselect behaviour).
 *
 * Multi-selection is driven purely by our own store, so on every shift-click we
 * also clear React Flow's built-in single selection and the single-entity focus
 * dim to keep both models from fighting each other.
 */
const useMultiSelectEntity = () => {
  const { setSelection, setAnchor, toggle, clear } = useMultiSelectionActions();
  const { drawer, handleCloseDrawer } = useProject();
  const { expandAll } = useExpandCollapsedLayers();

  const selectEntity = useCallback(
    async (nodeId: string) => {
      // Never multi-select unsaved (local) entities.
      if (isElementLocal(nodeId)) return;

      const isMultiSelectionActive =
        getMultiSelectionState().selectedIds.length > 0;

      if (isMultiSelectionActive) {
        // Continuing an active multi-selection: add or deselect.
        toggle(nodeId);
      } else {
        // Entering multi-selection. The anchor (entity opened in the drawer) is
        // only relevant while a drawer is open.
        const anchorId = drawer ? getMultiSelectionState().anchorId : null;

        // Follow the unsaved-changes handling before leaving the single entity.
        if (drawer) {
          try {
            await handleCloseDrawer();
          } catch {
            // User cancelled the discard-changes confirmation.
            return;
          }
        }

        const seedIds =
          anchorId && anchorId !== nodeId && !isElementLocal(anchorId)
            ? [anchorId, nodeId]
            : [nodeId];

        setSelection(seedIds);
      }

      // Drop React Flow's single selection + the focus dim so the multi-select
      // highlight is the only active visual state.
      canvasState().unselectActiveNode();
      canvasState().unselectActiveEdge();
      canvasMap.resetFocusedNode();
    },
    [drawer, handleCloseDrawer, setSelection, toggle],
  );

  /**
   * Selects every entity currently in the model (Cmd/Ctrl+A). Applies to the
   * whole entity set regardless of collapsed layers or scroll position, and
   * replaces any existing multi-selection with all N entities.
   */
  const selectAll = useCallback(async () => {
    const allEntityIds = canvasState()
      .nodes.filter(
        (node) =>
          node.type === NodesTypes.CustomNode && !isElementLocal(node.id),
      )
      .map((node) => node.id);

    if (allEntityIds.length === 0) return;

    // A drawer open means a single entity is being viewed/edited: follow the
    // unsaved-changes handling before switching to the full selection.
    if (drawer) {
      try {
        await handleCloseDrawer();
      } catch {
        // User cancelled the discard-changes confirmation.
        return;
      }
    }

    // Reveal every collapsed layer so all selected entities are visible.
    await expandAll();

    canvasState().unselectActiveNode();
    canvasState().unselectActiveEdge();
    canvasMap.resetFocusedNode();

    setSelection(allEntityIds);
  }, [drawer, handleCloseDrawer, setSelection, expandAll]);

  return {
    selectEntity,
    selectAll,
    clearMultiSelection: clear,
    setSingleSelectionAnchor: setAnchor,
  };
};

export default useMultiSelectEntity;
